import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// Base selector for email sent history state
const selectEmailSentHistoryState = (state: RootState) =>
  state.emailSentHistory;

// Selectors for sent emails
export const selectEmailSentHistory = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.history
);

export const selectEmailSentHistoryLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.loading
);

export const selectEmailSentHistoryError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.error
);

export const selectEmailSentHistorySuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.success
);

export const selectEmailSentHistoryPagination = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => ({
    total: emailSentHistory.total,
    page: emailSentHistory.page,
    limit: emailSentHistory.limit,
    totalPages: emailSentHistory.totalPages,
  })
);

export const selectCurrentCampaignId = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.currentCampaignId
);

export const selectEmailSentHistoryFilters = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.filters
);

// Selectors for draft emails
export const selectDraftEmails = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.draftEmails
);

export const selectDraftEmailsLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.draftLoading
);

export const selectDraftEmailsError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.draftError
);

export const selectDraftEmailsSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.draftSuccess
);

export const selectDraftEmailsPagination = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => ({
    total: emailSentHistory.draftTotal,
    page: emailSentHistory.draftPage,
    limit: emailSentHistory.draftLimit,
    totalPages: emailSentHistory.draftTotalPages,
  })
);

export const selectCurrentDraftCampaignId = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.currentDraftCampaignId
);

export const selectDraftEmailsFilters = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.draftFilters
);

// Selectors for send email
export const selectSendEmailLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendLoading
);

export const selectSendEmailError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendError
);

export const selectSendEmailSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendSuccess
);

export const selectSendEmailMessage = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendMessage
);

export const selectSendAllEmailLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendAllLoading
);

export const selectSendAllEmailError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendAllError
);

export const selectSendAllEmailSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendAllSuccess
);

export const selectSendAllEmailMessage = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendAllMessage
);

// Export selectors
export const selectExportDraftBlob = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportDraftBlob
);

export const selectExportDraftLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportDraftLoading
);

export const selectExportDraftError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportDraftError
);

export const selectExportDraftSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportDraftSuccess
);

export const selectExportSentBlob = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportSentBlob
);

export const selectExportSentLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportSentLoading
);

export const selectExportSentError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportSentError
);

export const selectExportSentSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.exportSentSuccess
);

// Selectors for send test email
export const selectSendTestEmailLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendTestLoading
);

export const selectSendTestEmailError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendTestError
);

export const selectSendTestEmailSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendTestSuccess
);

export const selectSendTestEmailMessage = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.sendTestMessage
);

// Selectors for email view
export const selectEmailViewLoading = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.emailViewLoading
);

export const selectEmailViewError = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.emailViewError
);

export const selectEmailViewSuccess = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.emailViewSuccess
);

export const selectEmailViewHtmlLink = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.emailViewHtmlLink
);

export const selectEmailViewDetails = createSelector(
  [selectEmailSentHistoryState],
  (emailSentHistory) => emailSentHistory.emailViewDetails
);

// Combined selectors for easier access
export const selectEmailSentHistoryData = createSelector(
  [
    selectEmailSentHistory,
    selectEmailSentHistoryLoading,
    selectEmailSentHistoryError,
    selectEmailSentHistorySuccess,
    selectEmailSentHistoryPagination,
  ],
  (history, loading, error, success, pagination) => ({
    history,
    loading,
    error,
    success,
    pagination,
  })
);

export const selectDraftEmailsData = createSelector(
  [
    selectDraftEmails,
    selectDraftEmailsLoading,
    selectDraftEmailsError,
    selectDraftEmailsSuccess,
    selectDraftEmailsPagination,
  ],
  (draftEmails, draftLoading, draftError, draftSuccess, pagination) => ({
    draftEmails,
    draftLoading,
    draftError,
    draftSuccess,
    pagination,
  })
);
